/**
 * At-rest encryption for locally-persisted binary blobs (the baked mockup assets).
 *
 * The mockup images synced to a device are multi-MB pixels sitting in IndexedDB. We
 * encrypt them with AES-GCM so the on-disk copy is ciphertext. The key is a
 * NON-EXTRACTABLE `CryptoKey` generated once and persisted in a dedicated IndexedDB store —
 * it can be used to encrypt/decrypt but never exported as raw bytes. Works identically on
 * web and the desktop webview (both have WebCrypto + IndexedDB).
 *
 * Best-effort: if WebCrypto is unavailable (e.g. SSR/tests without `crypto.subtle`),
 * `getBlobKey()` returns null and callers fall back to storing plaintext rather than crash.
 */

const KEY_DB = "genz-crypto";
const KEY_STORE = "keys";
const KEY_ID = "mockup-aes-gcm";
const IV_BYTES = 12;

function subtle(): SubtleCrypto | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c && c.subtle ? c.subtle : null;
}

function keyDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(KEY_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(KEY_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

/** A key read that DISTINGUISHES "definitely absent" from "the read itself failed" — the
 *  two must not be conflated (see `getBlobKey`): generating a fresh key over a store whose
 *  read merely errored would permanently orphan every blob encrypted with the real key. */
function idbGet<T>(db: IDBDatabase, key: string): Promise<{ ok: true; value: T | null } | { ok: false }> {
  return new Promise((resolve) => {
    try {
      const r = db.transaction(KEY_STORE, "readonly").objectStore(KEY_STORE).get(key);
      r.onsuccess = () => resolve({ ok: true, value: (r.result as T) ?? null });
      r.onerror = () => resolve({ ok: false });
    } catch {
      resolve({ ok: false });
    }
  });
}

/** Persist the key; resolves true only when the transaction COMMITTED. A swallowed write
 *  failure here was the worst storage bug found by the audit: the fresh key stayed cached
 *  in memory but never persisted, so the NEXT session generated a different key and every
 *  blob encrypted this session became permanently undecryptable. */
function idbPut(db: IDBDatabase, key: string, val: unknown): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(KEY_STORE, "readwrite");
      tx.objectStore(KEY_STORE).put(val, key);
      tx.oncomplete = () => resolve(true);
      tx.onabort = () => resolve(false);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

let cached: CryptoKey | null | undefined;

/**
 * The device's blob-encryption key — a non-extractable AES-GCM key, generated once and
 * reused across sessions (persisted in IndexedDB). Null when WebCrypto is unavailable.
 */
export async function getBlobKey(): Promise<CryptoKey | null> {
  if (cached !== undefined) return cached;
  const s = subtle();
  if (!s) return (cached = null);
  const db = await keyDb();
  if (db) {
    const read = await idbGet<CryptoKey>(db, KEY_ID);
    if (!read.ok) {
      // The read FAILED (transient IndexedDB error) — we cannot know whether a key exists.
      // Do NOT generate-and-store here: overwriting a real key would permanently orphan
      // every previously-encrypted mockup row (the "all my templates stopped rendering"
      // failure). Return null for THIS session only (plaintext fallback / decrypt skipped)
      // and leave the memo unset so the next call retries the read.
      return null;
    }
    if (read.value) return (cached = read.value);
  }
  // Definitely no key yet (a clean read said so) → first run on this device: generate one.
  // extractable=false → the raw key material can never leave the crypto module.
  const key = await s.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  if (db) {
    const persisted = await idbPut(db, KEY_ID, key);
    if (!persisted) {
      // The key did NOT commit. Caching it anyway would let this session encrypt blobs
      // that NO future session can ever decrypt (the key dies with this process). Treat
      // it like the failed-read case: no key this session, retry the whole flow next call.
      return null;
    }
  }
  return (cached = key);
}

/** Encrypt bytes → `iv(12) || ciphertext` (self-describing, so decrypt needs only the key). */
export async function encryptBytes(key: CryptoKey, bytes: Uint8Array): Promise<Uint8Array> {
  const s = subtle();
  if (!s) throw new Error("WebCrypto unavailable");
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = new Uint8Array(await s.encrypt({ name: "AES-GCM", iv }, key, bytes as unknown as BufferSource));
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv, 0);
  out.set(ct, iv.length);
  return out;
}

/** Decrypt an `iv || ciphertext` blob produced by {@link encryptBytes}. */
export async function decryptBytes(key: CryptoKey, blob: Uint8Array): Promise<Uint8Array> {
  const s = subtle();
  if (!s) throw new Error("WebCrypto unavailable");
  const iv = blob.subarray(0, IV_BYTES) as unknown as BufferSource;
  const ct = blob.subarray(IV_BYTES) as unknown as BufferSource;
  return new Uint8Array(await s.decrypt({ name: "AES-GCM", iv }, key, ct));
}

function bytesToBase64(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s);
}

function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Encrypt a UTF-8 string → base64 blob (for JSON records that must survive IndexedDB AND
 *  the desktop SQLite store, both of which round-trip strings but not raw byte arrays). */
export async function encryptString(key: CryptoKey, text: string): Promise<string> {
  return bytesToBase64(await encryptBytes(key, new TextEncoder().encode(text)));
}

/** Reverse {@link encryptString}. */
export async function decryptString(key: CryptoKey, b64: string): Promise<string> {
  return new TextDecoder().decode(await decryptBytes(key, base64ToBytes(b64)));
}

/** Reset the memoized key — tests only. */
export function _resetBlobKeyForTests(): void {
  cached = undefined;
}
